import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaTrashAlt, FaExclamationCircle, FaPlus, FaTimes, FaCheckCircle, FaHistory, FaChartLine, FaCalendarAlt, FaUser, FaFileAlt } from 'react-icons/fa';

export default function Mermas({ sucursalId, user, goBack }) {
    const [insumos, setInsumos] = useState([]);
    const [mermas, setMermas] = useState([]);
    const [form, setForm] = useState({ insumo_id: '', cantidad: '', motivo: '' });
    const [filtroFecha, setFiltroFecha] = useState('todos'); // hoy, semana, mes, todos

    useEffect(() => {
        fetchData();
    }, [sucursalId]);

    const fetchData = async () => {
        try {
            const resInsumos = await axios.get(`https://api.alquilacarbolivia.com/api/inventario/${sucursalId}`);
            const resMermas = await axios.get(`https://api.alquilacarbolivia.com/api/mermas/${sucursalId}`);
            setInsumos(resInsumos.data);
            setMermas(resMermas.data);
        } catch (error) {
            console.error('Error al cargar datos:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!form.insumo_id || !form.cantidad || !form.motivo) {
            Swal.fire({
                icon: 'warning',
                title: 'Datos incompletos',
                text: 'Por favor completa todos los campos',
                confirmButtonColor: '#f59e0b'
            });
            return;
        }

        const result = await Swal.fire({
            title: '¿Confirmar registro de merma?',
            html: `Se descontará <strong>${form.cantidad}</strong> del inventario`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, registrar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            await axios.post('https://api.alquilacarbolivia.com/api/mermas', {
                sucursal_id: sucursalId,
                usuario_id: user.id,
                ...form
            });
            Swal.fire({
                icon: 'success',
                title: '¡Registrado!',
                text: 'Merma registrada y stock actualizado',
                confirmButtonColor: '#10b981'
            });
            setForm({ insumo_id: '', cantidad: '', motivo: '' });
            fetchData();
        } catch (error) { 
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo registrar la merma',
                confirmButtonColor: '#ef4444'
            });
        }
    };

    // Calcular estadísticas
    const calcularEstadisticas = () => {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        
        const mermasHoy = mermas.filter(m => {
            const fechaMerma = new Date(m.fecha);
            fechaMerma.setHours(0, 0, 0, 0);
            return fechaMerma.getTime() === hoy.getTime();
        });

        const totalMermas = mermas.length;
        const mermasHoyCount = mermasHoy.length;

        return { totalMermas, mermasHoyCount };
    };

    const filtrarMermas = () => {
        const ahora = new Date();
        
        switch(filtroFecha) {
            case 'hoy':
                return mermas.filter(m => {
                    const fecha = new Date(m.fecha);
                    return fecha.toDateString() === ahora.toDateString();
                });
            case 'semana':
                const semanaAtras = new Date(ahora.getTime() - 7 * 24 * 60 * 60 * 1000);
                return mermas.filter(m => new Date(m.fecha) >= semanaAtras);
            case 'mes':
                const mesAtras = new Date(ahora.getTime() - 30 * 24 * 60 * 60 * 1000);
                return mermas.filter(m => new Date(m.fecha) >= mesAtras);
            default:
                return mermas;
        }
    };

    const stats = calcularEstadisticas();
    const mermasFiltradas = filtrarMermas();

    const motivosMasComunes = () => {
        const conteo = {};
        mermas.forEach(m => {
            const motivo = m.motivo.toLowerCase();
            conteo[motivo] = (conteo[motivo] || 0) + 1;
        });
        return Object.entries(conteo)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3);
    };

    const motivosTop = motivosMasComunes();

    return (
        <div className="container-fluid p-0" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header Mejorado */}
            <div style={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                padding: '1.5rem 2rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                        <button 
                            className="btn btn-light shadow-sm" 
                            onClick={goBack}
                            style={{ 
                                borderRadius: '12px',
                                padding: '0.5rem 1rem',
                                fontWeight: '600',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={e => e.target.style.transform = 'translateY(-2px)'}
                            onMouseLeave={e => e.target.style.transform = 'translateY(0)'}
                        >
                            <FaArrowLeft className="me-2"/> Volver
                        </button>
                        <div className="text-white">
                            <h3 className="m-0 fw-bold d-flex align-items-center gap-2">
                                <FaTrashAlt style={{ fontSize: '1.5rem' }}/> 
                                Control de Mermas
                            </h3>
                            <p className="m-0 opacity-75" style={{ fontSize: '0.9rem' }}>
                                Registro y seguimiento de desperdicios
                            </p>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 text-white">
                        <FaUser className="opacity-75"/>
                        <span className="fw-semibold">{user?.nombre || 'Usuario'}</span>
                    </div>
                </div>
            </div>

            <div className="p-4">
                {/* Tarjetas de estadísticas */}
                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                            <div className="card-body p-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="m-0 opacity-75 small">Total Mermas</p>
                                        <h3 className="m-0 fw-bold">{stats.totalMermas}</h3>
                                    </div>
                                    <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                                        <FaTrashAlt/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                            <div className="card-body p-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="m-0 opacity-75 small">Mermas Hoy</p>
                                        <h3 className="m-0 fw-bold">{stats.mermasHoyCount}</h3>
                                    </div>
                                    <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                                        <FaCalendarAlt/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                            <div className="card-body p-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="m-0 opacity-75 small">Motivo Frecuente</p>
                                        <h6 className="m-0 fw-bold text-truncate" style={{ maxWidth: '150px' }}>
                                            {motivosTop.length > 0 ? motivosTop[0][0] : 'N/A'}
                                        </h6>
                                    </div>
                                    <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                                        <FaChartLine/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    {/* Formulario de registro */}
                    <div className="col-lg-5">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', position: 'sticky', top: '20px' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                padding: '1.25rem',
                                borderRadius: '16px 16px 0 0'
                            }}>
                                <h5 className="m-0 text-white fw-bold d-flex align-items-center gap-2">
                                    <FaExclamationCircle/> Registrar Pérdida
                                </h5>
                            </div>
                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold text-muted small d-flex align-items-center gap-2">
                                            <FaFileAlt/> Insumo Afectado
                                        </label>
                                        <select 
                                            className="form-select form-select-lg" 
                                            required 
                                            value={form.insumo_id} 
                                            onChange={e => setForm({...form, insumo_id: e.target.value})}
                                            style={{ borderRadius: '10px', border: '2px solid #e2e8f0' }}
                                        >
                                            <option value="">Seleccione un insumo...</option>
                                            {insumos.map(i => (
                                                <option key={i.id} value={i.id}>
                                                    {i.nombre_insumo} ({i.cantidad} {i.unidad_medida} disponibles)
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold text-muted small">Cantidad Perdida</label>
                                        <input 
                                            type="number" 
                                            step="0.01" 
                                            className="form-control form-control-lg" 
                                            placeholder="0.00"
                                            required 
                                            value={form.cantidad} 
                                            onChange={e => setForm({...form, cantidad: e.target.value})}
                                            style={{ borderRadius: '10px', border: '2px solid #e2e8f0' }}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold text-muted small">Motivo de la Pérdida</label>
                                        <textarea 
                                            className="form-control" 
                                            rows="4" 
                                            placeholder="Describe el motivo: vencido, derramado, quemado, contaminado, etc."
                                            required 
                                            value={form.motivo} 
                                            onChange={e => setForm({...form, motivo: e.target.value})}
                                            style={{ borderRadius: '10px', border: '2px solid #e2e8f0' }}
                                        ></textarea>
                                    </div>

                                    {/* Motivos sugeridos */}
                                    {motivosTop.length > 0 && (
                                        <div className="mb-3">
                                            <p className="text-muted small mb-2">Motivos frecuentes:</p>
                                            <div className="d-flex flex-wrap gap-2">
                                                {motivosTop.map(([motivo, count], idx) => (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        className="btn btn-sm"
                                                        onClick={() => setForm({...form, motivo: motivo})}
                                                        style={{
                                                            background: '#fee2e2',
                                                            color: '#dc2626',
                                                            border: 'none',
                                                            borderRadius: '20px',
                                                            padding: '0.4rem 1rem',
                                                            fontSize: '0.85rem'
                                                        }}
                                                    >
                                                        {motivo} ({count})
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="alert alert-warning d-flex align-items-start gap-2" style={{ 
                                        borderRadius: '10px',
                                        background: '#fef3c7',
                                        border: 'none',
                                        color: '#92400e'
                                    }}>
                                        <FaExclamationCircle className="mt-1"/>
                                        <small>
                                            Esta acción descontará la cantidad especificada del inventario actual.
                                        </small>
                                    </div>

                                    <button 
                                        type="submit"
                                        className="btn btn-lg w-100 fw-bold shadow-sm"
                                        style={{
                                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                            border: 'none',
                                            color: 'white',
                                            borderRadius: '10px',
                                            padding: '0.75rem',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={e => {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 10px 20px rgba(239, 68, 68, 0.3)';
                                        }}
                                        onMouseLeave={e => {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                                        }}
                                    >
                                        <FaCheckCircle className="me-2"/> REGISTRAR BAJA
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Historial de mermas */}
                    <div className="col-lg-7">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                            <div className="card-header bg-white border-0 p-4" style={{ borderRadius: '16px 16px 0 0' }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="m-0 fw-bold d-flex align-items-center gap-2">
                                        <FaHistory style={{ color: '#ef4444' }}/> Historial de Mermas
                                    </h5>
                                </div>
                                
                                {/* Filtros */}
                                <div className="btn-group w-100" role="group">
                                    <button 
                                        type="button" 
                                        className={`btn ${filtroFecha === 'todos' ? 'btn-danger' : 'btn-outline-danger'}`}
                                        onClick={() => setFiltroFecha('todos')}
                                        style={{ borderRadius: '10px 0 0 10px' }}
                                    >
                                        Todos
                                    </button>
                                    <button 
                                        type="button" 
                                        className={`btn ${filtroFecha === 'hoy' ? 'btn-danger' : 'btn-outline-danger'}`}
                                        onClick={() => setFiltroFecha('hoy')}
                                    >
                                        Hoy
                                    </button>
                                    <button 
                                        type="button" 
                                        className={`btn ${filtroFecha === 'semana' ? 'btn-danger' : 'btn-outline-danger'}`}
                                        onClick={() => setFiltroFecha('semana')}
                                    >
                                        Última semana
                                    </button>
                                    <button 
                                        type="button" 
                                        className={`btn ${filtroFecha === 'mes' ? 'btn-danger' : 'btn-outline-danger'}`}
                                        onClick={() => setFiltroFecha('mes')}
                                        style={{ borderRadius: '0 10px 10px 0' }}
                                    >
                                        Último mes
                                    </button>
                                </div>
                            </div>

                            <div className="card-body p-0">
                                {mermasFiltradas.length === 0 ? (
                                    <div className="text-center py-5">
                                        <div style={{ fontSize: '4rem', color: '#cbd5e1' }}>
                                            <FaTrashAlt/>
                                        </div>
                                        <h5 className="text-muted mt-3">No hay mermas registradas</h5>
                                        <p className="text-muted">
                                            {filtroFecha === 'todos' 
                                                ? 'Comienza registrando las pérdidas de inventario'
                                                : 'No hay registros para el período seleccionado'
                                            }
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                        <table className="table table-hover mb-0">
                                            <thead style={{ 
                                                background: '#f8fafc', 
                                                position: 'sticky', 
                                                top: 0, 
                                                zIndex: 10,
                                                borderBottom: '2px solid #e2e8f0'
                                            }}>
                                                <tr>
                                                    <th className="fw-bold text-muted" style={{ padding: '1rem' }}>FECHA</th>
                                                    <th className="fw-bold text-muted" style={{ padding: '1rem' }}>INSUMO</th>
                                                    <th className="fw-bold text-muted text-center" style={{ padding: '1rem' }}>CANTIDAD</th>
                                                    <th className="fw-bold text-muted" style={{ padding: '1rem' }}>MOTIVO</th>
                                                    <th className="fw-bold text-muted" style={{ padding: '1rem' }}>USUARIO</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mermasFiltradas.map(m => (
                                                    <tr key={m.id}>
                                                        <td style={{ padding: '1rem' }}>
                                                            <div>
                                                                <div className="fw-semibold">
                                                                    {new Date(m.fecha).toLocaleDateString('es-BO', { 
                                                                        day: '2-digit', 
                                                                        month: 'short', 
                                                                        year: 'numeric' 
                                                                    })}
                                                                </div>
                                                                <div className="text-muted small">
                                                                    {new Date(m.fecha).toLocaleTimeString('es-BO', { 
                                                                        hour: '2-digit', 
                                                                        minute: '2-digit' 
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '1rem' }}>
                                                            <span className="fw-bold">{m.nombre_insumo}</span>
                                                        </td>
                                                        <td className="text-center" style={{ padding: '1rem' }}>
                                                            <span 
                                                                className="badge"
                                                                style={{
                                                                    background: '#fee2e2',
                                                                    color: '#dc2626',
                                                                    padding: '0.5rem 1rem',
                                                                    borderRadius: '20px',
                                                                    fontWeight: '700',
                                                                    fontSize: '0.9rem'
                                                                }}
                                                            >
                                                                -{m.cantidad}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '1rem' }}>
                                                            <div style={{
                                                                maxWidth: '250px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }} title={m.motivo}>
                                                                {m.motivo}
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '1rem' }}>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    borderRadius: '50%',
                                                                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '0.75rem'
                                                                }}>
                                                                    {m.usuario ? m.usuario.charAt(0).toUpperCase() : '?'}
                                                                </div>
                                                                <span className="fw-semibold small">{m.usuario || 'Sistema'}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {mermasFiltradas.length > 0 && (
                                <div className="card-footer bg-light border-0 text-center py-3">
                                    <small className="text-muted">
                                        Mostrando {mermasFiltradas.length} de {mermas.length} registros
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}